// Central money formatter. ONE place that turns a number into a currency string,
// so a non-US dealer sees their own currency symbol everywhere their money shows.
//
// Display-only: we never convert amounts. A dealer enters prices/costs in their
// own currency; we just format with the right symbol. Default is USD, so every
// existing (US) dealer is unaffected.
//
// The active currency is a module-level singleton (not React state) so plain
// helpers can be imported into any file, component or not. It's seeded from
// localStorage synchronously at import time to avoid a formatting flash on first
// paint, then AuthContext calls setCurrency() with the authoritative value from
// /auth/me once the user loads.

const SUPPORTED = ['USD', 'CAD', 'GBP', 'AUD'];

function readStored() {
  try {
    const v = (localStorage.getItem('scn-currency') || '').toUpperCase();
    return SUPPORTED.includes(v) ? v : 'USD';
  } catch {
    return 'USD';
  }
}

let _code = readStored();

export function setCurrency(code) {
  const c = String(code || '').toUpperCase();
  _code = SUPPORTED.includes(c) ? c : 'USD';
  try { localStorage.setItem('scn-currency', _code); } catch {}
}

export function getCurrency() {
  return _code;
}

// Build formatters lazily and cache per (code, min, max). Intl.NumberFormat is
// relatively expensive to construct and money() runs in a lot of render loops.
const _cache = {};
function formatter(min, max) {
  const key = `${_code}:${min}:${max}`;
  if (!_cache[key]) {
    try {
      _cache[key] = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: _code,
        currencyDisplay: 'narrowSymbol', // CAD/AUD -> "$", GBP -> "£", USD -> "$"
        minimumFractionDigits: min,
        maximumFractionDigits: max,
      });
    } catch {
      _cache[key] = null; // Intl missing / bad code -> fall back below
    }
  }
  return _cache[key];
}

function fmt(n, min, max) {
  const v = Number(n);
  const safe = Number.isFinite(v) ? v : 0;
  const f = formatter(min, max);
  if (f) return f.format(safe);
  return '$' + safe.toFixed(max); // last-resort fallback
}

// $1,234.50, the standard money string (symbol + grouped + 2 decimals).
export function money(n) {
  return fmt(n, 2, 2);
}

// $1,235, whole-dollar (no cents), for big round figures / charts.
export function money0(n) {
  return fmt(n, 0, 0);
}

// +$12.00 / -$12.00, leading sign, for deltas (profit, payout changes).
export function moneySigned(n) {
  const v = Number(n) || 0;
  return (v < 0 ? '-' : '+') + money(Math.abs(v));
}

// Just the symbol ("$", "£") for the current currency, for inline labels/prefixes.
export function currencySymbol() {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: _code, currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === 'currency');
    return (sym && sym.value) || '$';
  } catch {
    return '$';
  }
}
